/**
 * @prettier
 */
import { useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { BigNumber } from 'bignumber.js';
import { useEffect } from 'react';
import * as React from 'react';
import TagManager from 'react-gtm-module';
import { Button } from 'src/components/Button';
import { CartItem } from 'src/components/cart/CartItem';
import { Dialog } from 'src/components/Dialog';
import { Text } from 'src/components/Text';
import { LogEventTypes } from 'src/constants/LogEventType';
import { SubscriptionPlans } from 'src/constants/SubscriptionPlan';
import { SubscriptionStatuses } from 'src/constants/SubscriptionStatus';
import { AwsFacade, IsSignedInResults } from 'src/facade/aws/AwsFacade';
import { translate } from 'src/i18n/translate';
import { actions } from 'src/reducers';
import { createUiInteractionLogEvent } from 'src/services/logEvent/createUiInteractionLogEvent';
import { checkYourInternetConnectionAndTryAgain } from 'src/utils/alert/checkYourInternetConnectionAndTryAgain';
import { isWithinHours } from 'src/utils/hours/isWithinHours';
import { formatAsNumberCurrency } from 'src/utils/number/formatAsNumberCurrency';
import { useAction } from 'src/utils/react/useAction';
import { useSelector } from 'src/utils/react/useSelector';

export function CartDialog(): React.ReactElement | null {
    const classes = useStyles();
    const theme = useTheme();
    const isScreenBiggerThanMobile = useMediaQuery(theme.breakpoints.up('sm'));

    const restaurant = useSelector((state) => state.app.restaurant);
    const open = useSelector((state) => state.app.cartDialog.open);
    const checkoutOpen = useSelector((state) => state.app.checkoutDialog.open);

    const signedIn = useSelector((state) => state.app.signedIn);
    const cartItems = useSelector((state) => state.app.cartItems);
    const total = useSelector((state) => state.app.payment?.total);
    const currencyFormat = useSelector((state) => state.app.restaurantMenu?.currencyFormat);

    const openCheckoutDialog = useAction(actions.openCheckoutDialog);
    const openSignInDialog = useAction(actions.openSignInDialog);
    const closeCartDialog = useAction(actions.closeCartDialog);
    const openWarningAlert = useAction(actions.openWarningAlert);

    const subscriptionPlan = useSelector((state) => state.app.restaurant?.subscriptionPlan);
    const remainingDays = useSelector((state) => state.app.restaurant?.remainingDays);
    const subscriptionStatus = useSelector((state) => state.app.restaurant?.subscriptionStatus);
    const minimumOrderAmount = useSelector((state) => state.app.restaurant?.minimumOrderAmount);
    const maximumOrderAmount = useSelector((state) => state.app.restaurant?.maximumOrderAmount);

    const isServiceCutOffAndFreeTrial = remainingDays <= 0 && subscriptionPlan === SubscriptionPlans.FREE_TRIAL && !!subscriptionPlan;
    const isServiceCutOffAndNotPayPerUse = remainingDays <= 0 && subscriptionPlan !== SubscriptionPlans.PAY_PER_USE && !!subscriptionPlan;
    const plannedOrdersEnabled = useSelector((state) => state.app.restaurant?.plannedOrdersEnabled);
    const closed = !isWithinHours(restaurant.hours, restaurant.timeZone);
    const disabled = isServiceCutOffAndFreeTrial || isServiceCutOffAndNotPayPerUse || subscriptionStatus === SubscriptionStatuses.CANCELLED || (closed && !plannedOrdersEnabled);
    const isMinimumOrderAmountNotMet = minimumOrderAmount && BigNumber(minimumOrderAmount).isGreaterThan(total);
    const isMaximumOrderAmountMet = maximumOrderAmount && BigNumber(total).isGreaterThan(maximumOrderAmount);
    const dialogShouldBeClosed = cartItems.length === 0 || checkoutOpen;

    useEffect(() => {
        if (dialogShouldBeClosed) closeCartDialog();
    }, [dialogShouldBeClosed]);

    const signInAndOpenCheckout = async () => {
        if (disabled) return;

        if (isMinimumOrderAmountNotMet) {
            openWarningAlert({
                title: translate('Minimum order amount not met'),
                message: translate('Minimum order amount: @minimumOrderAmount', { minimumOrderAmount: formatAsNumberCurrency(minimumOrderAmount, currencyFormat) }),
            });
            return;
        }

        if (isMaximumOrderAmountMet) {
            openWarningAlert({
                title: translate('Maximum order amount reached'),
                message: translate('Maximum order amount: @maximumOrderAmount', { maximumOrderAmount: formatAsNumberCurrency(maximumOrderAmount, currencyFormat) }),
            });
            return;
        }

        if (!signedIn) {
            const result = await AwsFacade.isSignedIn(true);
            if (result === IsSignedInResults.NETWORK_ERROR) {
                checkYourInternetConnectionAndTryAgain();
            }
            if (result === IsSignedInResults.SIGNED_IN) {
                handleOpenCheckout();
            } else {
                openSignInDialog({ onSuccess: () => handleOpenCheckout() });
            }
        } else {
            handleOpenCheckout();
        }
    };

    const handleOpenCheckout = () => {
        if (restaurant.googleAnalyticsId) {
            const dataLayer = {
                event: 'Checkout',
                products: cartItems,
                total: total,
            } as const;
            const tagManagerData = {
                gtmId: restaurant.googleAnalyticsId,
                dataLayer,
            } as const;
            TagManager.initialize(tagManagerData);
        }
        createUiInteractionLogEvent({
            logEventType: LogEventTypes.USER_OPENED_CART_DIALOG,
        });
        openCheckoutDialog();
    };

    if (dialogShouldBeClosed) return null;

    return (
        <Dialog
            classes={{ dialog: classes.dialog, content: classes.dialogContent }}
            title={translate('My Order')}
            open={open}
            onClose={closeCartDialog}
            position={isScreenBiggerThanMobile ? 'right' : 'bottom'}
        >
            <div className={classes.cartContent}>
                <div className={classes.cartItemsContainer}>
                    {cartItems.map((cartItem) => {
                        return <CartItem key={cartItem.menuItemId} cartItem={cartItem} />;
                    })}
                </div>
                <div className={classes.cartBottom}>
                    <div className={classes.line}></div>
                    <div className={classes.amountContainer}>
                        <Text className={classes.amountText}>{translate('Amount to pay')}</Text>
                        <Text variant={'semiBold'} className={classes.amount}>
                            {formatAsNumberCurrency(total, currencyFormat)}
                        </Text>
                    </div>
                    {!!minimumOrderAmount && (
                        <Text className={classes.minimumOrderAmount}>
                            {translate('Minimum order amount')}: {formatAsNumberCurrency(minimumOrderAmount, currencyFormat)}
                        </Text>
                    )}
                    {!!maximumOrderAmount && (
                        <Text className={classes.maximumOrderAmount}>
                            {translate('Maximum order amount')}: {formatAsNumberCurrency(maximumOrderAmount, currencyFormat)}
                        </Text>
                    )}
                    <Button
                        classes={{ button: classes.button }}
                        onClick={() => {
                            closeCartDialog();
                            signInAndOpenCheckout();
                        }}
                        disabled={disabled}
                    >
                        {translate('Continue')}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

const useStyles = makeStyles((theme) => ({
    dialog: {
        width: '100%',
        height: '88vh',
        backgroundColor: theme.palette.primary.main,
        [theme.breakpoints.up('sm')]: {
            width: 420,
            height: '100vh',
            backgroundColor: 'white',
            maxHeight: '100vh',
        },
    },
    dialogContent: {
        maxHeight: '100vh',
    },
    totalText: {
        fontSize: 14,
        [theme.breakpoints.up('sm')]: {
            color: '#333333',
        },
    },
    total: {
        fontSize: 18,
        fontWeight: 'bold',
        [theme.breakpoints.up('sm')]: {
            color: '#333333',
        },
    },
    itemsCounter: {
        position: 'relative',
        display: 'flex',
        padding: '5px',
    },
    itemsQuantity: {
        position: 'absolute',
        display: 'grid',
        placeItems: 'center',
        fontSize: 14,
        width: 24,
        height: 24,
        backgroundColor: 'white',
        borderRadius: '50%',
        right: -10,
        top: -10,
        color: theme.palette.primary.main,
    },
    cartContent: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        [theme.breakpoints.down('xs')]: {
            maxHeight: '60vh',
        },
    },
    topBar: {
        position: 'relative',
        width: 70,
        height: 4,
        backgroundColor: 'rgba(0,0,0,0.5)',
        borderRadius: 4,
        marginTop: 8,
        marginBottom: 18,
        alignSelf: 'center',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    headerText: {
        fontSize: 24,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontWeight: 500,
        [theme.breakpoints.up('sm')]: {
            color: '#333333',
        },
    },
    cartItemsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 40,
        marginTop: 24,
        height: '50vh',
        overflow: 'auto',
        [theme.breakpoints.up('sm')]: {
            height: '100%',
        },
    },
    line: {
        width: '100%',
        height: 1,
        backgroundColor: 'rgba(0,0,0,0.2)',
        marginTop: 12,
    },
    amountContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        color: 'white',
        marginTop: 12,
        [theme.breakpoints.up('sm')]: {
            color: '#333333',
        },
    },
    button: {
        display: 'grid',
        placeItems: 'center',
        width: '100%',
        height: 60,
        fontSize: 20,
        fontWeight: 600,
        marginTop: 24,
        [theme.breakpoints.down('xs')]: {
            backgroundColor: 'white',
            color: theme.palette.primary.main,
        },
        '&:hover': {
            cursor: 'pointer',
        },
    },
    amountText: {
        fontSize: 20,
        color: 'white',
        [theme.breakpoints.up('sm')]: {
            color: '#0D3037',
        },
    },
    amount: {
        fontSize: 26,
        fontWeight: 700,
        color: 'white',
        [theme.breakpoints.up('sm')]: {
            color: '#0D3037',
        },
    },
    cartBottom: {},
    minimumOrderAmount: {},
    maximumOrderAmount: {},
}));
